import { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { API } from "../../App";
import { Button } from "../ui/button";
import { X, ShieldCheck, Loader2 } from "lucide-react";
import { extractApiError } from "../../lib/apiError";

/** Super-admin-only modal for toggling fine-grained capabilities on an admin user.
 * The user prop must be role="admin"; super_admin roles are auto-granted every
 * permission by the backend and can't be edited here. */
export default function PermissionsModal({ user, onClose, onSaved }) {
  const [available, setAvailable] = useState(null); // [{key, label, description}]
  const [selected, setSelected] = useState(new Set(user?.permissions || []));
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    let alive = true;
    axios.get(`${API}/admin/permissions/available`)
      .then((res) => { if (alive) setAvailable(res.data?.permissions || []); })
      .catch((err) => {
        toast.error(extractApiError(err, "Failed to load permission catalog"));
        onClose?.();
      });
    return () => { alive = false; };
  }, [onClose]);

  const toggle = (key) => {
    setSelected(prev => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key); else next.add(key);
      return next;
    });
  };

  const selectAll = () => setSelected(new Set((available || []).map(p => p.key)));
  const clearAll = () => setSelected(new Set());

  const save = async () => {
    setSaving(true);
    try {
      const permissions = Array.from(selected);
      await axios.patch(`${API}/admin/users/${user.user_id}/permissions`, { permissions });
      toast.success(`Permissions updated for ${user.name}`);
      onSaved?.();
      onClose?.();
    } catch (err) {
      toast.error(extractApiError(err, "Failed to save permissions"));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm p-4" data-testid="permissions-modal">
      <div className="bg-[#121212] border border-white/10 rounded-sm w-full max-w-2xl max-h-[90vh] overflow-hidden flex flex-col">
        <div className="flex items-center justify-between p-5 border-b border-white/5">
          <div>
            <h3 className="text-lg font-semibold text-white flex items-center gap-2">
              <ShieldCheck className="w-5 h-5 text-[#C5A059]" />
              Permissions — {user.name}
            </h3>
            <p className="text-white/40 text-xs mt-0.5">{user.email}</p>
          </div>
          <button onClick={onClose} className="text-white/40 hover:text-white" data-testid="permissions-modal-close">
            <X className="w-5 h-5" />
          </button>
        </div>

        {!available ? (
          <div className="flex items-center justify-center py-16 text-white/50 gap-2">
            <Loader2 className="w-5 h-5 animate-spin" /> Loading…
          </div>
        ) : (
          <>
            <div className="p-5 border-b border-white/5 flex items-center gap-2">
              <button
                onClick={selectAll}
                className="text-[10px] px-2.5 py-1 rounded-sm border border-[#C5A059]/40 text-[#C5A059] hover:bg-[#C5A059]/10"
                data-testid="permissions-select-all"
              >Grant all</button>
              <button
                onClick={clearAll}
                className="text-[10px] px-2.5 py-1 rounded-sm border border-white/20 text-white/70 hover:bg-white/5"
                data-testid="permissions-clear-all"
              >Clear all</button>
              <span className="ml-auto text-white/40 text-xs">{selected.size} / {available.length} enabled</span>
            </div>

            <div className="flex-1 overflow-y-auto p-5 space-y-2">
              {available.map((perm) => {
                const on = selected.has(perm.key);
                return (
                  <label
                    key={perm.key}
                    className={`flex items-start gap-3 p-3 rounded-sm border cursor-pointer transition-colors ${on ? "bg-[#C5A059]/10 border-[#C5A059]/40" : "bg-[#0F0F0F] border-white/5 hover:border-white/20"}`}
                    data-testid={`permission-row-${perm.key}`}
                  >
                    <input
                      type="checkbox"
                      checked={on}
                      onChange={() => toggle(perm.key)}
                      className="mt-1 accent-[#C5A059] w-4 h-4"
                      data-testid={`permission-toggle-${perm.key}`}
                    />
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        <span className="text-white text-sm font-medium">{perm.label}</span>
                        <code className="text-[9px] text-white/30 font-mono">{perm.key}</code>
                      </div>
                      <p className="text-white/50 text-xs mt-0.5 leading-relaxed">{perm.description}</p>
                    </div>
                  </label>
                );
              })}
            </div>

            <div className="p-5 border-t border-white/5 flex items-center justify-end gap-2">
              <Button variant="ghost" onClick={onClose} disabled={saving} className="text-white/70">Cancel</Button>
              <Button onClick={save} disabled={saving} className="btn-gold" data-testid="permissions-save">
                {saving ? "Saving…" : "Save permissions"}
              </Button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
